import { Link } from "react-router-dom";
import { formatCallDuration, type Call } from "@shared/modules/x-appels";
import { Icon } from "../../../core/components/ui/icon";
import { CallBadge } from "./badges";
import { prettyPhone } from "./phone-links";

const fr = "fr-CA";

function when(value: string | number): { day: string; time: string } {
  const d = new Date(value);
  return {
    day: d.toLocaleDateString(fr, { day: "numeric", month: "short" }),
    time: `${d.getHours()} h ${String(d.getMinutes()).padStart(2, "0")}`,
  };
}

/**
 * Rangée d'appel : l'heure, qui, ce qu'il voulait, combien de temps, ce que
 * ça a donné. Un point ambré signale un message ou un rappel pas encore pris
 * en charge par l'équipe.
 */
export function CallRow({ call }: { call: Call }) {
  const { day, time } = when(call.startedAt);
  const todo =
    !call.handled &&
    (call.outcome === "message" || call.outcome === "rappel" || call.status === "manque");
  const classes = [
    "call-row",
    call.status === "manque" && "call-row--missed",
    todo && "call-row--todo",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <Link to={`/appels/${call.id}`} className={classes}>
      <span className={`call-row__icon call-row__icon--${call.status}`} aria-hidden="true">
        <Icon name={call.status === "manque" ? "alert-circle" : "phone"} size={18} />
      </span>
      <span className="call-row__when">
        <span className="call-row__day">{day}</span>
        <span className="call-row__time">{time}</span>
      </span>
      <span className="call-row__main">
        <span className="call-row__name">
          {call.callerName ?? prettyPhone(call.fromNumber)}
          {todo ? <span className="call-row__todo" title="À traiter" /> : null}
        </span>
        <span className="call-row__sub">
          {call.status === "manque"
            ? "Appel manqué — pas de réponse"
            : (call.intent ?? call.summary ?? (call.status === "en_cours" ? "En cours…" : "—"))}
          {call.callerName ? ` · ${prettyPhone(call.fromNumber)}` : ""}
        </span>
      </span>
      <span className="call-row__duration">
        {call.durationSec > 0 ? formatCallDuration(call.durationSec) : "—"}
      </span>
      <span className="call-row__badge">
        <CallBadge status={call.status} outcome={call.outcome} />
      </span>
      <span className="call-row__chevron" aria-hidden="true">
        <Icon name="chevron-right" size={18} />
      </span>
    </Link>
  );
}
